/**
 * Barcode rendering and label printing utilities.
 *
 * Rendering picks a symbology automatically from the value via
 * `detectSymbology()` — EAN-13 / EAN-8 / ITF-14 (numeric GS1 retail &
 * logistics) or CODE128 (ASCII fallback for warehouse/alphanumeric
 * SKUs). Rules are strict and non-overlapping; if none match, the
 * helpers throw `UNSUPPORTED_BARCODE_SYMBOLOGY` so the caller can
 * surface a user-visible error instead of printing a broken label.
 *
 * Printing: `generateBarcodeLabelsHTML(items, { showPrice })` returns a
 * full HTML document sized for standard 40×58mm thermal labels, one
 * label per page with a page-break between each. Pair it with
 * `openPrintWindow(html)` from `printUtils.js` so both desktop and
 * mobile print the same way.
 */
import JsBarcode from 'jsbarcode';
import { openPrintWindow } from './printUtils';

const CODE128_MAX_LENGTH = 80;

const eanCheckDigit = (data, oddWeight, evenWeight) => {
  let total = 0;
  for (let i = 0; i < data.length; i += 1) {
    const n = Number(data[i]);
    total += n * (i % 2 === 0 ? oddWeight : evenWeight);
  }
  const remainder = total % 10;
  return remainder === 0 ? 0 : 10 - remainder;
};

const isDigits = (s, len) =>
  typeof s === 'string' && s.length === len && /^\d+$/.test(s);

const validateEan13 = (v) =>
  isDigits(v, 13) && eanCheckDigit(v.slice(0, 12), 1, 3) === Number(v[12]);

const validateEan8 = (v) =>
  isDigits(v, 8) && eanCheckDigit(v.slice(0, 7), 3, 1) === Number(v[7]);

const validateItf14 = (v) =>
  isDigits(v, 14) && eanCheckDigit(v.slice(0, 13), 3, 1) === Number(v[13]);

// CODE39 alphabet: digits, uppercase A-Z, space, and -.$/+%
const CODE39_RE = /^[0-9A-Z\-. $/+%]+$/;
const isCode39Compatible = (v) =>
  typeof v === 'string' && v !== '' && CODE39_RE.test(v);

/**
 * Classify a barcode string into a JsBarcode format name. Returns one
 * of 'EAN13' | 'EAN8' | 'ITF14' | 'CODE39' | 'CODE128', or null when
 * the value can't be printed (non-ASCII or over CODE128_MAX_LENGTH).
 *
 * `preferred` (optional): override for the ASCII fallback — pass
 * 'CODE39' to render CODE39-compatible values as CODE39 instead of
 * CODE128. EAN/ITF validation still runs first since their strict
 * formats are unambiguous.
 *
 * Mirrors `detect_symbology` in `backend/api/utils/barcode_utils.py`
 * so the UI's pre-flight matches what the server considers printable.
 */
export const detectSymbology = (value, { preferred } = {}) => {
  if (typeof value !== 'string' || value === '') return null;
  if (validateEan13(value)) return 'EAN13';
  if (validateEan8(value)) return 'EAN8';
  if (validateItf14(value)) return 'ITF14';
  if (preferred === 'CODE39' && isCode39Compatible(value)) return 'CODE39';
  if (value.length <= CODE128_MAX_LENGTH) {
    // eslint-disable-next-line no-control-regex
    if (/^[\x00-\x7F]+$/.test(value)) return 'CODE128';
  }
  return null;
};

/**
 * Convenience wrapper — true when the value maps to a symbology we can
 * actually render. Use to gate Print buttons in the UI.
 */
export const isPrintableBarcode = (value) => detectSymbology(value) !== null;

export const UNSUPPORTED_BARCODE_SYMBOLOGY = 'UNSUPPORTED_BARCODE_SYMBOLOGY';

/**
 * Render a barcode into an <svg> element in the live DOM. The format
 * is picked automatically by `detectSymbology`. Throws
 * UNSUPPORTED_BARCODE_SYMBOLOGY when the value is not printable so the
 * caller can show an error instead of an empty label.
 */
export const renderBarcodeSVG = (svgEl, barcodeValue, options = {}) => {
  if (!svgEl || !barcodeValue) return;
  const value = String(barcodeValue);
  const { preferredSymbology, ...jsBarcodeOptions } = options;
  const format = detectSymbology(value, { preferred: preferredSymbology });
  if (!format) {
    const err = new Error(UNSUPPORTED_BARCODE_SYMBOLOGY);
    err.code = UNSUPPORTED_BARCODE_SYMBOLOGY;
    throw err;
  }
  JsBarcode(svgEl, value, {
    format,
    displayValue: true,
    font: 'monospace',
    fontSize: 14,
    textMargin: 4,
    width: 2,
    height: 60,
    margin: 6,
    ...jsBarcodeOptions,
  });
};

/**
 * Server-side-safe: generate a standalone <svg> string for a barcode.
 * Used inside the print HTML template, where we can't mount a DOM node
 * first and then inspect it. Returns '' when the value can't be
 * printed — callers that care (label sheets) should pre-flight with
 * `isPrintableBarcode` and surface a user-visible error.
 */
export const generateBarcodeSVGString = (barcodeValue, options = {}) => {
  if (!barcodeValue) return '';
  const value = String(barcodeValue);
  const { preferredSymbology, ...jsBarcodeOptions } = options;
  const format = detectSymbology(value, { preferred: preferredSymbology });
  if (!format) return '';
  const svgNS = 'http://www.w3.org/2000/svg';
  const node = document.createElementNS(svgNS, 'svg');
  JsBarcode(node, value, {
    format,
    displayValue: true,
    font: 'monospace',
    fontSize: 14,
    textMargin: 4,
    width: 2,
    height: 60,
    margin: 6,
    xmlDocument: document,
    ...jsBarcodeOptions,
  });
  return new XMLSerializer().serializeToString(node);
};

const escapeHtml = (str) =>
  String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') return '';
  const n = Number(price);
  if (Number.isNaN(n)) return '';
  return `₪${n.toFixed(2)}`;
};

/**
 * Build a print-ready HTML document containing thermal labels for a list
 * of products. Each entry looks like:
 *
 *   { barcode, productName, price?, labelCount? }
 *
 * options:
 *   - showPrice: boolean    — render the price line on each label
 *   - labelWidth: string    — CSS width (default "58mm")
 *   - labelHeight: string   — CSS height (default "40mm")
 */
export const generateBarcodeLabelsHTML = (items, options = {}) => {
  const {
    showPrice = true,
    labelWidth = '58mm',
    labelHeight = '40mm',
    preferredSymbology,
  } = options;

  const labels = [];
  (items || []).forEach((item) => {
    if (!isPrintableBarcode(item.barcode)) return;
    const count = Math.max(1, parseInt(item.labelCount || 1, 10));
    const svg = generateBarcodeSVGString(item.barcode, {
      width: 2,
      height: 50,
      fontSize: 12,
      margin: 2,
      preferredSymbology,
    });
    const priceLine =
      showPrice && item.price ? `<div class="price">${escapeHtml(formatPrice(item.price))}</div>` : '';

    for (let i = 0; i < count; i += 1) {
      labels.push(`
        <div class="label">
          <div class="name">${escapeHtml(item.productName || '')}</div>
          ${priceLine}
          <div class="barcode">${svg}</div>
        </div>
      `);
    }
  });

  return `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="utf-8" />
  <title>Barcode Labels</title>
  <style>
    @page {
      size: ${labelWidth} ${labelHeight};
      margin: 0;
    }
    * { box-sizing: border-box; }
    html, body {
      margin: 0; padding: 0;
      font-family: Arial, "Segoe UI", sans-serif;
      color: #000;
      background: #fff;
    }
    .label {
      width: ${labelWidth};
      height: ${labelHeight};
      padding: 2mm;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      overflow: hidden;
      page-break-after: always;
      break-after: page;
      text-align: center;
    }
    .label:last-child { page-break-after: auto; break-after: auto; }
    .name {
      font-size: 10pt;
      font-weight: 700;
      line-height: 1.1;
      max-height: 28%;
      overflow: hidden;
      word-break: break-word;
      width: 100%;
    }
    .price {
      font-size: 11pt;
      font-weight: 800;
      margin: 1mm 0;
    }
    .barcode {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 1;
    }
    .barcode svg { max-width: 100%; height: auto; }
    @media print {
      .label { margin: 0; }
    }
  </style>
</head>
<body>
  ${labels.join('\n')}
</body>
</html>`;
};

/**
 * Convenience: render labels + open the print window.
 */
export const printBarcodeLabels = (items, options = {}) => {
  const html = generateBarcodeLabelsHTML(items, options);
  openPrintWindow(html);
};
